import * as React from 'react';

import { Tag } from 'antd';
import { Column, Notification, Space, Text } from 'farmhub-ui-core';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from 'app';
import { dataEmpty, Empty, InfoCard, Table } from 'components';
import { findProductPricePolicy, selectProductUnit } from 'slice';
import {
  formatMoney,
  genWholesaleUnit,
  getDummyArray,
  renderTableSkeleton,
} from 'utils/function';

interface ProductPricePolicyTableProps {
  record: any;
}

const ProductPricePolicyTable = ({
  record,
}: ProductPricePolicyTableProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const productUnit = useAppSelector(selectProductUnit);
  const [isLoading, setIsLoading] = React.useState(false);
  const [productPricePolicies, setProductPricePolicies] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
  });

  const handleChangePagination = (page: number, pageSize?: number) => {
    setPagination(prevData => {
      return {
        current: page,
        pageSize: pageSize || prevData.pageSize,
      };
    });
  };

  const columns = [
    {
      width: 160,
      dataIndex: 'product',
      key: 'product',
      render: (_, row: any) => {
        const standardPriceUnit = row.priceUnit?.find(item => item.isStandard);

        return (
          <InfoCard
            img={row.product.avatar}
            info={
              <Text color="main" size="xxs">
                {genWholesaleUnit({
                  unitPrefix: productUnit.prefixObj[standardPriceUnit?.prefix],
                  unitWeight: standardPriceUnit?.weight,
                  unitPostfix:
                    productUnit.postfixObj[standardPriceUnit?.postfix],
                })}
              </Text>
            }
            name={
              <Text
                lineNumber={1}
                size="xs"
                title={row.product.name}
                weight="bold"
              >
                {row.product.name}
              </Text>
            }
          />
        );
      },
    },
    {
      width: 190,
      colSpan: 400,
      dataIndex: 'product',
      key: 'id',
      render: (_, product) => {
        const { priceUnit } = product;

        return (
          <Column
            alignItems="flex-end"
            justifyContent="flex-end"
            styles={{ gap: 6 }}
          >
            {priceUnit.map(item => (
              <CTag key={item.id}>
                {formatMoney(item.price)}đ/
                {genWholesaleUnit({
                  unitPrefix: productUnit.prefixObj[item.prefix],
                  unitWeight: item.weight,
                  unitPostfix: productUnit.postfixObj[item.postfix],
                })}
              </CTag>
            ))}
          </Column>
        );
      },
    },
  ];

  React.useEffect(() => {
    const initData = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const result: any = await dispatch(
          findProductPricePolicy({
            select: [
              'id',
              'pricePolicyID',
              'price',
              'standardPrice',
              'createdAt',
              'status',
              'priceUnit',
              'product.id',
              'product.name',
              'product.price',
              'product.avatar',
              'product.unitPrefix',
              'product.unitWeight',
              'product.unitPostfix',
            ],
            relations: ['product'],
            order: { createdAt: 0 },
            where: { pricePolicyID: record.pricePolicyID },
            page: pagination.current,
            take: pagination.pageSize,
          })
        ).unwrap();
        const { data, pagination: paginationResult } = result;
        setProductPricePolicies(data);
        setTotal(paginationResult.total || 0);
      } catch (error: any) {
        Notification('error', 'Tải sản phẩm thất bại', error);
      } finally {
        setIsLoading(false);
      }
    };

    initData();
  }, [dispatch, pagination, record]);

  return (
    <>
      <Table
        columns={isLoading ? renderTableSkeleton(tableSkeleton) : columns}
        dataSource={isLoading ? getDummyArray(10) : productPricePolicies}
        locale={{
          emptyText: (
            <Empty
              description="Không tìm thấy sản phẩm"
              img={dataEmpty.CATEGORY}
              minHeight={280}
            />
          ),
        }}
        pagination={{
          defaultCurrent: 1,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          onChange: handleChangePagination,
        }}
        showHeader={false}
      />
      <Space height={12} />
    </>
  );
};

export default ProductPricePolicyTable;

const tableSkeleton = [
  {
    type: 'image',
    width: 150,
  },
  {
    type: 'input',
    width: 150,
  },
];

const CTag = styled(Tag)`
  padding: 4px 8px;
  border-radius: 6px;
  border: 0;
  background-color: #ffffff;
  color: ${props => props.theme.colors.primary.main};
  font-size: ${props => props.theme.font.size.xs};
  position: relative;

  &::before {
    content: ' ';
    width: 100%;
    height: 100%;
    background: ${props => props.theme.colors.primary.main};

    opacity: 0.1;
    pointer-events: auto;
    position: absolute;
    top: 0;
    left: 0;
  }
`;
